import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Manages expand/collapse state during search.
 * - Auto-expands parent nodes when search matches children
 * - Tracks user overrides (manual expand/collapse during search)
 * - Resets overrides when search is cleared
 */
export const useSearchExpandState = (
  baseExpandedItems: string[],
  expandedOnSearch: string[],
  isSearchActive: boolean,
  onExpand: (items: string[]) => void
) => {
  const [userCollapsed, setUserCollapsed] = useState<Set<string>>(new Set());
  const prevIsSearchActive = useRef(isSearchActive);

  if (prevIsSearchActive.current && !isSearchActive) {
    setUserCollapsed(new Set());
  }
  prevIsSearchActive.current = isSearchActive;

  const expandedItems = useMemo(() => {
    if (!isSearchActive) {
      return baseExpandedItems;
    }

    const result = new Set(baseExpandedItems);

    expandedOnSearch.forEach(item => {
      if (!userCollapsed.has(item)) {
        result.add(item);
      }
    });

    return Array.from(result);
  }, [baseExpandedItems, expandedOnSearch, userCollapsed, isSearchActive]);

  const handleExpand = useCallback(
    (nextExpandedItems: string[]) => {
      if (!isSearchActive) {
        onExpand(nextExpandedItems);
        return;
      }

      const nextSet = new Set(nextExpandedItems);
      const autoSet = new Set(expandedOnSearch);

      const newUserCollapsed = new Set<string>();
      autoSet.forEach(item => {
        if (!nextSet.has(item)) {
          newUserCollapsed.add(item);
        }
      });
      setUserCollapsed(newUserCollapsed);

      const itemsToPersist = nextExpandedItems.filter(
        item => !autoSet.has(item)
      );
      onExpand(itemsToPersist);
    },
    [expandedOnSearch, isSearchActive, onExpand]
  );

  return { expandedItems, handleExpand };
};
